import type { OfficePresetDecorationConfig } from "@bezoned/shared/schemas/office/office-preset-manifest.schema.js";
import { useGLTF } from "@react-three/drei";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { Box3, Mesh, Vector3 } from "three";
import type { Group, Object3D } from "three";

type BasicDecorationProps = OfficePresetDecorationConfig & {
  castShadow: boolean;
  receiveShadow: boolean;
  modelPath: string;
};

export const GeneralDecoration = ({
  castShadow,
  receiveShadow,
  modelPath,
  position,
  rotation,
  scale,
}: BasicDecorationProps) => {
  const groupRef = useRef<Group>(null);
  const gltf = useGLTF(modelPath);

  const { scene: processedScene, center } = useMemo(() => {
    const scene = gltf.scene.clone(true);
    scene.updateMatrixWorld(true);

    const box = new Box3().setFromObject(scene);
    const centerPoint = box.getCenter(new Vector3());

    return { center: centerPoint, scene };
  }, [gltf.scene]);

  const clonedGltf = useCallback(
    () => processedScene.clone(true),
    [processedScene]
  );

  useEffect(() => {
    const clonedModel = clonedGltf();
    clonedModel.traverse((child: Object3D) => {
      if (child instanceof Mesh) {
        child.castShadow = castShadow;
        child.receiveShadow = receiveShadow;
      }
    });
    clonedModel.position.set(-center.x, -center.y, -center.z);

    groupRef.current?.add(clonedModel);

    return () => {
      groupRef.current?.remove(clonedModel);
    };
  }, [clonedGltf, castShadow, receiveShadow, center.x, center.y, center.z]);

  return (
    <group
      position={position as [number, number, number]}
      ref={groupRef}
      rotation={rotation as [number, number, number]}
      scale={scale as [number, number, number]}
    />
  );
};
